import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ChevronRight, ChevronDown, TrendingUp, TrendingDown, Target, Trash2 } from 'lucide-react';
import api from '../../services/api';

// Desempenho com veredito: janelas de 4/7/14/30 dias, campanha → conjunto → anúncio, cada item julgado contra a
// meta da inBase (semáforo + Escalar/Manter/Pausar/Aguardar com o motivo). "Propor" cria proposta na aba Ações.

const fmtBRL = (v) => (v === null || v === undefined ? '—' : Number(v).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: Number(v) >= 1000 ? 0 : 2 }));
const fmtNum = (v) => (v === null || v === undefined ? '—' : Number(v).toLocaleString('pt-BR'));
const fmtDia = (s) => { const m = String(s || '').match(/^(\d{4})-(\d{2})-(\d{2})/); return m ? `${m[3]}/${m[2]}/${m[1].slice(2)}` : s; };
const erroMsg = (err) => err?.response?.data?.error || err?.message;

const COR_TEXTO = { verde: 'text-green-700', amarelo: 'text-amber-600', vermelho: 'text-red-600' };
const COR_BORDA = { verde: 'border-l-green-500', amarelo: 'border-l-amber-400', vermelho: 'border-l-red-500' };
const VEREDITO = {
  escalar: ['Escalar', 'bg-green-100 text-green-800'],
  manter: ['Manter', 'bg-blue-50 text-blue-700'],
  pausar: ['Pausar', 'bg-red-100 text-red-700'],
  aguardar: ['Aguardar', 'bg-gray-100 text-gray-600'],
  sem_meta: ['Sem meta', 'bg-gray-100 text-gray-500'],
};
const ROTULO_NIVEL = { campaign: 'Campanha', adset: 'Conjunto', ad: 'Anúncio' };

function PillVeredito({ item }) {
  let [label, cls] = VEREDITO[item.veredito] || ['—', 'bg-gray-100 text-gray-500'];
  if (item.veredito === 'escalar' && item.nivel === 'ad') label = 'Vencedor';
  if (item.veredito === 'manter' && item.cor === 'amarelo') { label = 'Observar'; cls = 'bg-amber-100 text-amber-800'; }
  return <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-semibold whitespace-nowrap ${cls}`}>{label}</span>;
}

function Tendencia({ item }) {
  const v = item.anterior?.variacao_custo_pct;
  if (v === null || v === undefined) return <span className="text-gray-300">—</span>;
  const piorou = v > 0;   // custo subiu = pior
  const Icon = piorou ? TrendingUp : TrendingDown;
  return (
    <span className={`inline-flex items-center gap-0.5 ${piorou ? 'text-red-600' : 'text-green-700'}`} title={`Janela anterior: ${fmtBRL(item.anterior.custo_resultado)} por conversa`}>
      <Icon size={12} />{v > 0 ? '+' : ''}{v}%
    </span>
  );
}

function KpiCor({ label, value, sub, cor }) {
  return (
    <div className={`rounded-xl border border-gray-100 bg-white p-4 border-l-4 ${COR_BORDA[cor] || 'border-l-gray-200'}`}>
      <p className="text-xs font-medium text-gray-500">{label}</p>
      <p className={`text-2xl font-bold mt-1 ${COR_TEXTO[cor] || 'text-gray-800'}`}>{value}</p>
      {sub && <p className="text-xs text-gray-400 mt-1">{sub}</p>}
    </div>
  );
}

function Linha({ item, nivel, abertos, alternar, propor, propondo }) {
  const chave = `${item.nivel}:${item.id}`;
  const temFilhos = item.filhos?.length > 0;
  const aberto = abertos.has(chave);
  const podePropor = item.id && (item.veredito === 'pausar' || (item.veredito === 'escalar' && item.nivel !== 'ad'));
  return (
    <>
      <tr className={`border-t border-gray-50 align-top ${nivel === 0 ? 'bg-white' : nivel === 1 ? 'bg-gray-50/40' : 'bg-gray-50/80'}`}>
        <td className="px-3 py-2" style={{ paddingLeft: 12 + nivel * 22 }}>
          <div className="flex items-start gap-1">
            {temFilhos ? (
              <button onClick={() => alternar(chave)} className="mt-0.5 text-gray-400 hover:text-gray-700" aria-label={aberto ? 'Recolher' : 'Abrir'}>
                {aberto ? <ChevronDown size={15} /> : <ChevronRight size={15} />}
              </button>
            ) : <span className="w-[15px]" />}
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <span className={`${nivel === 0 ? 'font-semibold' : 'font-medium'} text-gray-800`}>{item.nome}</span>
                <span className="text-[10px] uppercase tracking-wide text-gray-400">{ROTULO_NIVEL[item.nivel]}</span>
                {item.nivel === 'campaign' && item.status && item.status !== 'ACTIVE' && <span className="text-[10px] text-gray-400">({item.status === 'PAUSED' ? 'pausada' : item.status.toLowerCase()})</span>}
                {item.nivel === 'campaign' && item.empreendimento && <span className="text-[10px] text-gray-500 bg-gray-100 rounded px-1.5">{item.empreendimento}</span>}
              </div>
              <p className="text-xs text-gray-500 mt-0.5">{item.motivo}</p>
              {item.alertas?.map((a, i) => <p key={i} className="text-xs text-amber-700 mt-0.5">⚠ {a}</p>)}
            </div>
          </div>
        </td>
        <td className="px-3 py-2"><PillVeredito item={item} /></td>
        <td className="text-right px-3 py-2 whitespace-nowrap">{fmtBRL(item.investimento)}</td>
        <td className="text-right px-3 py-2">{fmtNum(item.resultados)}</td>
        <td className={`text-right px-3 py-2 whitespace-nowrap font-medium ${COR_TEXTO[item.cor_custo] || 'text-gray-500'}`}>{fmtBRL(item.custo_resultado)}</td>
        <td className="text-right px-3 py-2 text-xs whitespace-nowrap"><Tendencia item={item} /></td>
        <td className={`text-right px-3 py-2 whitespace-nowrap ${COR_TEXTO[item.cor_ctr] || 'text-gray-500'}`}>{item.ctr != null ? `${item.ctr}%` : '—'}</td>
        <td className="text-right px-3 py-2 whitespace-nowrap text-gray-600">{fmtBRL(item.cpm)}</td>
        <td className="text-right px-3 py-2 whitespace-nowrap text-gray-600">{item.nivel === 'campaign' ? `${fmtNum(item.visitas)} / ${fmtNum(item.vendas)}` : ''}</td>
        <td className="text-right px-3 py-2">
          {podePropor && (
            <button onClick={() => propor(item)} disabled={propondo}
              className={`text-xs px-2 py-1 rounded-md border whitespace-nowrap disabled:opacity-50 ${item.veredito === 'pausar' ? 'border-red-200 text-red-700 hover:bg-red-50' : 'border-green-200 text-green-700 hover:bg-green-50'}`}>
              {item.veredito === 'pausar' ? 'Propor pausa' : 'Propor +20%'}
            </button>
          )}
        </td>
      </tr>
      {aberto && item.filhos.map((f) => <Linha key={`${f.nivel}:${f.id}`} item={f} nivel={nivel + 1} abertos={abertos} alternar={alternar} propor={propor} propondo={propondo} />)}
    </>
  );
}

const CAMPOS = [
  ['custo_resultado_max', 'Custo máx. por conversa/lead (R$)', 'A meta. Até aqui = verde.'],
  ['tolerancia_pct', 'Tolerância (%)', 'Até meta + X% = amarelo (observar); acima = vermelho (pausar).'],
  ['ctr_min', 'CTR mínimo (%)', 'Abaixo disso o painel avisa que o criativo/público está fraco (com 1.000+ impressões).'],
  ['amostra_gasto_min', 'Gasto mínimo para julgar (R$)', 'Abaixo disso o item fica em "Aguardar" — pouco dado é ruído.'],
  ['escalar_folga_pct', 'Escalar: custo abaixo da meta em (%)', 'Ex.: 25 = escalar se o custo estiver 25% ou mais abaixo da meta…'],
  ['escalar_resultados_min', 'Escalar: mínimo de resultados', '…e tiver pelo menos esse número de conversas/leads na janela.'],
];

function FormMeta({ inicial, onSalvar, salvando, onRemover }) {
  const [f, setF] = useState(() => Object.fromEntries(CAMPOS.map(([k]) => [k, inicial?.[k] ?? ''])));
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
      {CAMPOS.map(([k, label, dica]) => (
        <label key={k} className="text-sm">
          <span className="text-gray-700 font-medium">{label}</span>
          <input type="number" step="any" className="mt-1 w-full border border-gray-200 rounded-lg px-3 py-1.5 text-sm" value={f[k]}
            onChange={(e) => setF({ ...f, [k]: e.target.value })} />
          <span className="text-xs text-gray-400">{dica}</span>
        </label>
      ))}
      <div className="sm:col-span-2 lg:col-span-3 flex gap-2">
        <button onClick={() => onSalvar(f)} disabled={salvando} className="bg-primary text-white px-4 py-1.5 rounded-lg text-sm disabled:opacity-60">Salvar</button>
        {onRemover && <button onClick={onRemover} className="flex items-center gap-1 text-sm text-red-600 px-3 py-1.5"><Trash2 size={14} /> Remover (volta a usar a padrão)</button>}
      </div>
    </div>
  );
}

function Metas({ isAdmin }) {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ['trafego-metas'], queryFn: () => api.get('/trafego/metas').then(r => r.data.data) });
  const { data: emps } = useQuery({ queryKey: ['trafego-emps'], queryFn: () => api.get('/trafego/empreendimentos').then(r => r.data.data) });
  const [novoEmp, setNovoEmp] = useState('');
  const atualizar = () => { qc.invalidateQueries({ queryKey: ['trafego-metas'] }); qc.invalidateQueries({ queryKey: ['trafego-desempenho'] }); };
  const salvar = useMutation({
    mutationFn: ({ emp, f }) => api.put(`/trafego/metas/${emp || 'padrao'}`, f),
    onSuccess: () => { atualizar(); setNovoEmp(''); },
    onError: (err) => alert('Erro: ' + erroMsg(err)),
  });
  const remover = useMutation({
    mutationFn: (emp) => api.delete(`/trafego/metas/${emp}`),
    onSuccess: atualizar,
    onError: (err) => alert('Erro: ' + erroMsg(err)),
  });
  if (!data) return <div className="text-sm text-gray-500">Carregando metas…</div>;
  const resumo = (m) => `${fmtBRL(m.custo_resultado_max)} por conversa · tolerância ${m.tolerancia_pct}% · CTR mín. ${m.ctr_min ?? '—'}% · julga a partir de ${fmtBRL(m.amostra_gasto_min)} · escala com ${m.escalar_folga_pct}% abaixo e ${m.escalar_resultados_min}+ resultados`;
  const usados = new Set(data.empreendimentos.map(e => e.empreendimento_id));

  if (!isAdmin) {
    return (
      <div className="text-sm text-gray-600 space-y-1">
        <p><strong>Padrão:</strong> {resumo(data.padrao)}</p>
        {data.empreendimentos.map(e => <p key={e.id}><strong>{e.empreendimento_nome}:</strong> {resumo(e)}</p>)}
        <p className="text-xs text-gray-400">Só admin altera as metas.</p>
      </div>
    );
  }
  return (
    <div className="space-y-5">
      <div>
        <h4 className="text-sm font-semibold text-gray-700 mb-2">Meta padrão (vale para toda campanha sem meta própria)</h4>
        <FormMeta key={`p-${data.padrao.updated_at}`} inicial={data.padrao} salvando={salvar.isPending} onSalvar={(f) => salvar.mutate({ emp: null, f })} />
      </div>
      {data.empreendimentos.map((e) => (
        <div key={e.id} className="border-t border-gray-100 pt-4">
          <h4 className="text-sm font-semibold text-gray-700 mb-2">Meta do {e.empreendimento_nome}</h4>
          <FormMeta key={`${e.id}-${e.updated_at}`} inicial={e} salvando={salvar.isPending} onSalvar={(f) => salvar.mutate({ emp: e.empreendimento_id, f })}
            onRemover={() => { if (window.confirm(`Remover a meta própria do ${e.empreendimento_nome}?`)) remover.mutate(e.empreendimento_id); }} />
        </div>
      ))}
      <div className="border-t border-gray-100 pt-4">
        <h4 className="text-sm font-semibold text-gray-700 mb-2">Meta própria para um empreendimento</h4>
        <p className="text-xs text-gray-500 mb-2">Ex.: um empreendimento mais caro aceita custo por conversa maior. Vale para as campanhas ligadas a ele (coluna Empreendimento no Painel).</p>
        <select className="border border-gray-200 rounded-lg text-sm px-3 py-1.5 mb-3" value={novoEmp} onChange={(e) => setNovoEmp(e.target.value)}>
          <option value="">— escolher empreendimento —</option>
          {(emps || []).filter(e => !usados.has(e.id)).map((e) => <option key={e.id} value={e.id}>{e.nome}</option>)}
        </select>
        {novoEmp && <FormMeta key={novoEmp} inicial={data.padrao} salvando={salvar.isPending} onSalvar={(f) => salvar.mutate({ emp: novoEmp, f })} />}
      </div>
    </div>
  );
}

function abrirAte(campanhas, nivelMax) {
  const s = new Set();
  for (const c of campanhas) {
    if (nivelMax >= 1) s.add(`campaign:${c.id}`);
    if (nivelMax >= 2) for (const a of c.filhos) s.add(`adset:${a.id}`);
  }
  return s;
}

export default function Desempenho({ isAdmin }) {
  const qc = useQueryClient();
  const [dias, setDias] = useState(7);
  const [ate, setAte] = useState('');   // '' = até ontem
  const [soAcao, setSoAcao] = useState(false);
  const [abertos, setAbertos] = useState(new Set());
  const [verMetas, setVerMetas] = useState(false);
  const { data, isLoading, error } = useQuery({
    queryKey: ['trafego-desempenho', dias, ate],
    queryFn: () => api.get('/trafego/desempenho', { params: { dias, ate: ate || undefined } }).then(r => r.data.data),
  });
  const propor = useMutation({
    mutationFn: (item) => api.post('/trafego/desempenho/propor', { nivel: item.nivel, id: item.id, veredito: item.veredito, motivo: item.motivo }),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ['trafego-acoes'] });
      const a = r.data.data;
      alert(a.ja_existia ? `Já existe a proposta #${a.id} para esse item na aba Ações.` : `Proposta #${a.id} criada — um admin aprova na aba "Ações e regras".`);
    },
    onError: (err) => alert('Não foi possível propor: ' + erroMsg(err)),
  });
  const alternar = (k) => setAbertos((s) => { const n = new Set(s); if (n.has(k)) n.delete(k); else n.add(k); return n; });

  if (isLoading) return <div className="text-gray-500 p-6">Carregando…</div>;
  if (error) return <div className="text-red-600 p-6">Erro: {erroMsg(error)}</div>;
  const t = data.totais;
  const m = data.meta_padrao;
  const precisaAcao = (i) => i.veredito === 'pausar' || i.veredito === 'escalar' || i.cor === 'amarelo' || i.filhos?.some(precisaAcao);
  const campanhas = soAcao ? data.campanhas.filter(precisaAcao) : data.campanhas;
  const semDadosRecentes = !data.campanhas.length && data.ultimo_dia_com_dados && data.ultimo_dia_com_dados < data.janela.inicio;

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="inline-flex rounded-lg border border-gray-200 bg-white p-0.5">
          {[4, 7, 14, 30].map((d) => (
            <button key={d} onClick={() => setDias(d)}
              className={`px-4 py-1.5 text-sm rounded-md ${dias === d ? 'bg-primary text-white font-semibold' : 'text-gray-600 hover:bg-gray-50'}`}>{d} dias</button>
          ))}
        </div>
        <div className="flex flex-wrap items-center gap-3 text-sm text-gray-600">
          <span>{fmtDia(data.janela.inicio)} a {fmtDia(data.janela.fim)} <span className="text-gray-400">(comparando com {fmtDia(data.janela.anterior.inicio)}–{fmtDia(data.janela.anterior.fim)})</span></span>
          {ate
            ? <button onClick={() => setAte('')} className="text-primary underline">voltar para até ontem</button>
            : data.ultimo_dia_com_dados && data.ultimo_dia_com_dados < data.janela.fim && (
              <button onClick={() => setAte(data.ultimo_dia_com_dados)} className="text-primary underline">ver até o último dia com gasto ({fmtDia(data.ultimo_dia_com_dados)})</button>
            )}
        </div>
      </div>

      {semDadosRecentes && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 text-sm text-yellow-800">
          Nenhum anúncio gastou nessa janela — o último gasto foi em {fmtDia(data.ultimo_dia_com_dados)}. Clique em "ver até o último dia com gasto" para julgar o último período que rodou.
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        <KpiCor label="Investimento" value={fmtBRL(t.investimento)} sub={`${fmtNum(t.impressoes)} impressões`} />
        <KpiCor label="Conversas / leads" value={fmtNum(t.resultados)} />
        <KpiCor label="Custo por conversa" value={fmtBRL(t.custo_resultado)} sub={`meta ${fmtBRL(m.custo_resultado_max)}`} cor={t.cor_custo} />
        <KpiCor label="CTR (link)" value={t.ctr != null ? `${t.ctr}%` : '—'} sub={m.ctr_min != null ? `mínimo ${m.ctr_min}%` : null} cor={t.cor_ctr} />
        <KpiCor label="Visitas (de anúncio)" value={fmtNum(t.visitas)} />
        <KpiCor label="Vendas (de anúncio)" value={fmtNum(t.vendas)} sub={t.vendas ? `VGV ${fmtBRL(t.vgv)}` : null} />
      </div>

      <div className="flex flex-wrap items-center gap-2 text-sm">
        <span className="text-gray-500">Campanhas:</span>
        {['escalar', 'manter', 'pausar', 'aguardar', 'sem_meta'].filter(k => data.contagem[k]).map((k) => (
          <span key={k} className={`px-2 py-0.5 rounded-full text-xs font-semibold ${VEREDITO[k][1]}`}>{VEREDITO[k][0]}: {data.contagem[k]}</span>
        ))}
        <span className="flex-1" />
        <label className="flex items-center gap-1.5 text-gray-600 cursor-pointer">
          <input type="checkbox" checked={soAcao} onChange={(e) => setSoAcao(e.target.checked)} /> Só o que pede ação
        </label>
        <button onClick={() => setAbertos(abrirAte(data.campanhas, 1))} className="text-xs border border-gray-200 rounded-md px-2 py-1 bg-white">Abrir conjuntos</button>
        <button onClick={() => setAbertos(abrirAte(data.campanhas, 2))} className="text-xs border border-gray-200 rounded-md px-2 py-1 bg-white">Abrir tudo</button>
        <button onClick={() => setAbertos(new Set())} className="text-xs border border-gray-200 rounded-md px-2 py-1 bg-white">Fechar</button>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-x-auto">
        <table className="w-full text-sm min-w-[980px]">
          <thead className="bg-gray-50 text-gray-500 text-xs uppercase">
            <tr>
              <th className="text-left px-3 py-2">Campanha → conjunto → anúncio</th><th className="text-left px-3">Veredito</th>
              <th className="text-right px-3">Investimento</th><th className="text-right px-3">Conversas</th>
              <th className="text-right px-3">Custo/conversa</th><th className="text-right px-3" title="Custo por conversa comparado com a janela anterior">vs anterior</th>
              <th className="text-right px-3">CTR</th><th className="text-right px-3">CPM</th>
              <th className="text-right px-3" title="Visitas agendadas e vendas ligadas à campanha pelo telefone do cliente">Visitas / vendas</th><th className="px-3" />
            </tr>
          </thead>
          <tbody>
            {campanhas.map((c) => <Linha key={`campaign:${c.id}`} item={c} nivel={0} abertos={abertos} alternar={alternar} propor={(i) => propor.mutate(i)} propondo={propor.isPending} />)}
            {!campanhas.length && <tr><td colSpan={10} className="px-4 py-6 text-center text-gray-400">{soAcao && data.campanhas.length ? 'Nada pedindo ação nessa janela' : 'Sem gasto nessa janela'}</td></tr>}
          </tbody>
        </table>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-100">
        <button onClick={() => setVerMetas(!verMetas)} className="w-full flex items-center justify-between px-4 py-3 text-sm font-semibold text-gray-700">
          <span className="flex items-center gap-2"><Target size={16} /> Metas e critérios do veredito</span>
          {verMetas ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
        </button>
        {verMetas && <div className="px-4 pb-4"><Metas isAdmin={isAdmin} /></div>}
      </div>

      <p className="text-xs text-gray-500">
        O veredito só recomenda: "Propor" cria uma proposta na aba <strong>Ações e regras</strong>, e nada muda na Meta até um admin aprovar.
        "Escalar" sobe 20% o orçamento de quem o segura (conjunto ou campanha) — mais que isso de uma vez reinicia o aprendizado da Meta.
        Conversa = conversa iniciada no WhatsApp + lead de formulário.
      </p>
    </div>
  );
}
